import React, { useState } from 'react'

import { Button, Divider, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { brainstormText } from 'content/brainstormText'

import { DressUpPromptResponse } from 'models/ideaModels'

import { useToastContext } from 'contexts/ToastProvider'

import PromptCarouselComponent from 'pages/RoundRobin/components/Carousel'

import FacilitationHeader from 'components/FacilitationHeader'
import GeneratedExampleDropdown from 'components/GeneratedExampleDropdown'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import { commonStyles } from 'styles/common.styles'

import { styles } from './Section2.styles'

export const TEST_ID = 'dress-up-section2'

export type Props = {
	createPrompt: () => void
	dressUpPrompt?: DressUpPromptResponse
	loading: boolean
	reloadIdeas: (
		updatedTraits: string,
		companyIndex: number
	) => Promise<boolean>
}

const buildResponseItem = (solution: string) => {
	const stringSol = solution
		.split(/(\d.)/)
		?.splice(1)
		.filter((x) => isNaN(parseInt(x)))
	return (
		<Box bgcolor={'rgba(231, 248, 255, 1)'} padding={'1rem'}>
			<ol data-testid="gen-ai-response-list">
				{stringSol.map((el: string, i: number) => (
					<Typography
						fontSize={16}
						component="li"
						sx={{ ...commonStyles.header, ...styles.header }}
						key={`ai-solution-${i}`}
						variant="body1"
						style={{
							margin: '10px 0 10px 0',
						}}
					>
						{el}
					</Typography>
				))}
			</ol>
		</Box>
	)
}
const Section2 = ({ dressUpPrompt, ...props }: Props) => {
	const { showAlert } = useToastContext()
	const [businessComparisonIndex, setBusinessComparisonIndex] = useState(0)
	const [editPromptMode, setEditPromptMode] = useState(false)
	const [isGenAiLoading, setIsGenAiLoading] = useState(false)
	const [showAIExampleIdea, setShowAIExampleIdea] = useState(false)

	const onClickRefresh = () => {
		setBusinessComparisonIndex(0)
		props.createPrompt()
	}

	const [temporalPromptsEditMode, setTemporalPrompts] = useState<string[]>([])
	const setPromptValue = (value: string) => {
		const newTemporalPrompts = [...temporalPromptsEditMode]
		newTemporalPrompts[businessComparisonIndex] = value
		setTemporalPrompts(newTemporalPrompts)
	}
	const currentPrompts =
		dressUpPrompt?.data.companies.map((item) => item.traits) ?? []

	const onClickEditPrompt = () => {
		setTemporalPrompts(currentPrompts)
		setEditPromptMode(true)
	}

	const onClickCancelEditPrompt = () => {
		setEditPromptMode(false)
	}

	const showEditPromptSuccessAlert = () => {
		if (!showAlert) {
			return
		}

		showAlert({
			objectType: 'editPrompt',
			severity: ToastSeverity.SUCCESS,
		})
	}

	const showDefaultErrorAlert = () => {
		if (!showAlert) {
			return
		}

		showAlert({
			objectType: 'defaultError',
			severity: ToastSeverity.ERROR,
		})
	}

	const hasTraitChanged = (
		lastEditedIdea: HTMLCollectionOf<HTMLTextAreaElement>
	) => {
		if (!dressUpPrompt || lastEditedIdea.length < 1) {
			return false
		}

		return (
			dressUpPrompt.data.companies[businessComparisonIndex].traits !==
			lastEditedIdea[0].value
		)
	}

	const onClickSaveEditPrompt = async () => {
		const lastEditedIdea = document.getElementsByTagName('textarea')
		if (!hasTraitChanged(lastEditedIdea)) {
			return
		}

		setIsGenAiLoading(true)

		try {
			const proceeded = await props.reloadIdeas(
				lastEditedIdea[0].value,
				businessComparisonIndex
			)

			if (!proceeded) {
				return
			}

			setEditPromptMode(false)
			showEditPromptSuccessAlert()
		} catch (error) {
			showDefaultErrorAlert()
		} finally {
			setIsGenAiLoading(false)
		}
	}

	return (
		<>
			<Section2Title />
			<UserEducationModal />
			<Box display={'flex'}>
				<Box marginRight={'2rem'} sx={styles.sideBar} />
				<Box
					display="flex"
					flexDirection="column"
					gap="2rem"
					flexGrow="1"
					paddingY={{ xs: '0', sm: '1rem' }}
				>
					<Box
						display={'flex'}
						flexDirection={{
							xs: 'column',
							sm: 'row',
						}}
						justifyContent={'space-between'}
						alignItems={{
							xs: 'flex-start',
							sm: 'center',
						}}
						gap={'1rem'}
					>
						<Typography
							data-testid={`${TEST_ID}-user-directions-verbiage`}
							sx={commonStyles.header}
						>
							While parity comes from looking at competitors;
							innovation comes from exploring best practices
							across different industries. Consider what these
							cross-industry companies are known for to inspire
							new approaches to this problem.
						</Typography>
					</Box>
					<FacilitationHeader
						onRefresh={onClickRefresh}
						onEdit={editPromptMode ? undefined : onClickEditPrompt}
						isLoading={props.loading}
						refreshTestId={`${TEST_ID}__refresh-button`}
						editTestId={`${TEST_ID}-edit-prompt`}
					/>
					<Box marginLeft="2rem" marginRight="2rem">
						{props.loading === true ? (
							<LoadingSpinner />
						) : (
							<>
								<Box sx={styles.carouselWrapper}>
									<Box
										flexDirection={{
											xs: 'column',
											sm: 'row',
										}}
										className="button-wrapper"
									>
										{dressUpPrompt?.data.companies.map(
											(item, index) => (
												<React.Fragment key={index}>
													<Button
														data-testid={`${TEST_ID}__business-comparison-${index}`}
														className={
															index ===
															businessComparisonIndex
																? 'selected'
																: ''
														}
														onClick={() =>
															setBusinessComparisonIndex(
																index
															)
														}
														disabled={
															editPromptMode
														}
													>
														{item.name}
													</Button>
													{index <
														dressUpPrompt.data
															.companies.length -
															1 && (
														<Divider
															orientation="vertical"
															variant="middle"
															flexItem={true}
														/>
													)}
												</React.Fragment>
											)
										)}
									</Box>
								</Box>
								<Box
									data-testid={`${TEST_ID}__business-comparison-traits`}
								>
									{dressUpPrompt && (
										<PromptCarouselComponent
											disabledArrows={editPromptMode}
											setCurrentIndex={
												setBusinessComparisonIndex
											}
											currentIndex={
												businessComparisonIndex
											}
											currentPrompts={dressUpPrompt.data.companies.map(
												(item) => item.traits
											)}
											promptsEditMode={
												temporalPromptsEditMode
											}
											editable={editPromptMode}
											setPromptValue={setPromptValue}
											names={dressUpPrompt.data.companies.map(
												(item) => item.name
											)}
											sxCardOverrides={{
												backgroundColor:
													'rgba(231, 248, 255, 1)',
											}}
										/>
									)}
								</Box>
							</>
						)}
					</Box>
					{editPromptMode ? (
						<Box
							display="flex"
							flexDirection={{
								xs: 'column',
								sm: 'row',
							}}
							gap="2rem"
						>
							<Button
								color="secondary"
								variant="contained"
								data-testid={`${TEST_ID}__edit-save-button`}
								onClick={async () => {
									setIsGenAiLoading(true)
									await onClickSaveEditPrompt()
									setIsGenAiLoading(false)
								}}
							>
								<Typography>SAVE CHANGES</Typography>
							</Button>
							<Button
								variant="text"
								color="secondary"
								data-testid={`${TEST_ID}__edit-cancel-button`}
								onClick={onClickCancelEditPrompt}
							>
								<Typography>CANCEL</Typography>
							</Button>
						</Box>
					) : null}
					<Typography
						data-testid={`${TEST_ID}-genai-idea-dropdown`}
						sx={commonStyles.header}
					>
						{brainstormText.global.ideasDropdown}
					</Typography>
					<GeneratedExampleDropdown
						gameTypeEnum={GAME_TYPE.DRESS_UP}
						headerText={''}
						isLoading={isGenAiLoading}
						customResponse={buildResponseItem(
							dressUpPrompt?.data?.companies?.[
								businessComparisonIndex
							]?.advices ?? ''
						)}
						showAIExampleIdea={showAIExampleIdea}
						setShowAIExampleIdea={setShowAIExampleIdea}
					/>
				</Box>
			</Box>
		</>
	)
}
export default Section2
